import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MailWarning, RotateCw, Trash2, Copy, ChevronDown, ChevronUp, CheckCheck } from "lucide-react";

/**
 * Admin-only card on the Clients page. Lists users invited but not yet active.
 * Allows resend + cancel per row. Silent (renders nothing) when empty.
 */
export default function PendingInvites({ onChange }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [copied, setCopied] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/pending-invites");
      setItems(data);
    } catch (err) {
      // fail quietly — this shouldn't block the Clients page
      if (err?.response?.status !== 403) {
        toast.error(err?.response?.data?.detail || "Could not load pending invites");
      }
    }
  };
  useEffect(() => { load(); }, []);

  const resend = async (row) => {
    setBusyId(row.user_id);
    try {
      const { data } = await api.post(`/auth/resend-invite/${row.user_id}`);
      toast.success(`Invite resent to ${row.email}`);
      // stash new URL onto the row for quick copy
      setItems((prev) => prev.map((r) => (r.user_id === row.user_id ? { ...r, invite_url: data.invite_url } : r)));
      load();
      onChange && onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resend failed");
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (row) => {
    if (!window.confirm(`Cancel invite for ${row.email}? They will no longer be able to accept it.`)) return;
    setBusyId(row.user_id);
    try {
      await api.delete(`/auth/invite/${row.user_id}`);
      toast.success("Invite cancelled");
      load();
      onChange && onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancel failed");
    } finally {
      setBusyId(null);
    }
  };

  const copy = async (row) => {
    if (!row.invite_url) return;
    try {
      await navigator.clipboard.writeText(row.invite_url);
      setCopied(row.user_id);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };

  const relTime = (iso) => {
    if (!iso) return "—";
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  if (items.length === 0) return null;

  const expiredCount = items.filter((r) => r.status === "expired").length;

  return (
    <div className="hbt-card overflow-hidden mb-8" data-testid="pending-invites-panel">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full p-5 flex items-center justify-between hover:bg-black/[0.02] transition-colors"
        data-testid="pending-invites-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: expiredCount > 0 ? "rgba(200,90,60,0.1)" : "rgba(26,54,45,0.08)" }}>
            <MailWarning className="w-5 h-5" style={{ color: expiredCount > 0 ? "var(--terracotta, #C85A3C)" : "var(--forest)" }} />
          </div>
          <div className="text-left">
            <div className="hbt-label">Pending invites</div>
            <div className="font-display text-lg font-semibold" data-testid="pending-invites-count">
              {items.length} awaiting activation
              {expiredCount > 0 && <span className="text-sm font-normal ml-2" style={{ color: "var(--terracotta, #C85A3C)" }}>· {expiredCount} expired</span>}
            </div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-neutral-500" /> : <ChevronDown className="w-4 h-4 text-neutral-500" />}
      </button>

      {open && (
        <div className="border-t" style={{ borderColor: "var(--border)" }}>
          <table className="hbt-table">
            <thead>
              <tr>
                <th>Person</th>
                <th>Client</th>
                <th>Last sent</th>
                <th>Status</th>
                <th className="text-right pr-6">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.user_id} data-testid={`pending-invite-row-${r.user_id}`}>
                  <td>
                    <div className="font-medium">{r.name || "—"}</div>
                    <div className="text-xs text-neutral-500">{r.email}</div>
                  </td>
                  <td className="text-sm">{r.client_name || "—"}</td>
                  <td className="text-xs text-neutral-500" title={r.last_sent_at || ""}>{relTime(r.last_sent_at)}</td>
                  <td>
                    {r.status === "expired"
                      ? <span className="pill" style={{ background: "rgba(200,90,60,0.15)", color: "var(--terracotta,#C85A3C)" }} data-testid={`pending-status-${r.user_id}`}>Expired</span>
                      : <span className="pill forest" data-testid={`pending-status-${r.user_id}`}>Pending</span>}
                  </td>
                  <td className="text-right pr-6 space-x-1 whitespace-nowrap">
                    {r.invite_url && (
                      <button onClick={() => copy(r)} className="btn-outline text-xs inline-flex items-center gap-1" data-testid={`pending-copy-${r.user_id}`} title="Copy backup URL">
                        {copied === r.user_id ? <CheckCheck className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      </button>
                    )}
                    <button
                      onClick={() => resend(r)}
                      disabled={busyId === r.user_id}
                      className="btn-outline text-xs inline-flex items-center gap-1"
                      data-testid={`pending-resend-${r.user_id}`}
                    >
                      <RotateCw className={`w-3 h-3 ${busyId === r.user_id ? "animate-spin" : ""}`} /> Resend
                    </button>
                    <button
                      onClick={() => cancel(r)}
                      disabled={busyId === r.user_id}
                      className="btn-outline text-xs inline-flex items-center gap-1"
                      data-testid={`pending-cancel-${r.user_id}`}
                      style={{ color: "var(--terracotta, #C85A3C)" }}
                    >
                      <Trash2 className="w-3 h-3" /> Cancel
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Named helper: parent pages call this after they trigger an invite to refresh state.
PendingInvites.refreshKey = () => `${Date.now()}`;
